<template>
  <div class="card card-body">

    <button class="btn btn-outline-secondary mb-2" @click="downloadSample">
      Download Sample Excel
    </button>

    <input type="file" accept=".xlsx,.xls" class="form-control mb-3" @change="handleFile" />

    <input v-if="rows.length" class="form-control mb-2" placeholder="Search..." v-model="search" />

    <div v-if="isImporting" class="mb-3">
      <div class="progress">
        <div class="progress-bar progress-bar-striped progress-bar-animated" :style="{ width: progress + '%' }">
          {{ progress }}%
        </div>
      </div>
      <small class="text-muted">
        Imported {{ processedRows }} of {{ totalRows }} rows
      </small>
    </div>

    <div class="table-responsive" style="max-height: 420px; overflow-y:auto">
      <table class="table table-sm table-bordered">
        <thead class="table-light">
          <tr>
            <th>#</th>
            <th>Account Name</th>
            <th>Account Type</th>
            <th>Account Category</th>
            <th>Opening Balance</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(r, i) in filteredRows" :key="i" :class="rowClass(r)">
            <td>{{ i + 1 }}</td>

            <td>
              <input class="form-control form-control-sm" v-model="r.name" @input="validateRow(r)" />
            </td>

            <td>
              <select class="form-select form-select-sm" v-model="r.type" @change="validateRow(r)">
                <option value="">Select</option>
                <option v-for="t in ACCOUNT_TYPES" :key="t">{{ t }}</option>
              </select>
            </td>

            <td>
              <select class="form-select form-select-sm" v-model="r.accountCategory" @change="validateRow(r)">
                <option value="">Select</option>
                <option v-for="c in ACCOUNT_CATEGORIES" :key="c">{{ c }}</option>
              </select>
            </td>

            <td>
              <input type="number" class="form-control form-control-sm" v-model.number="r.openingBalance"
                @input="validateRow(r)" />
            </td>

            <td>
              <span v-if="r._status === 'SUCCESS'" class="text-success">✔ {{ r._remark }}</span>
              <span v-else-if="r._status === 'FAILED'" class="text-danger">✖ {{ r._remark }}</span>
              <span v-else class="text-muted">Pending</span>
            </td>

            <td>
              <button class="btn btn-sm btn-danger" @click="deleteRow(i)">✖</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <button v-if="deletedRows.length" class="btn btn-warning mt-2" @click="undoDelete">
      Undo Delete
    </button>

    <button class="btn btn-success mt-3" @click="submitRows">
      Submit Valid Rows
    </button>

  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import accountApi from '@/api/accountApi'

const ACCOUNT_TYPES = [
  'ASSET',
  'LIABILITY',
  'INCOME',
  'EXPENSE',
  'EQUITY'
]

const ACCOUNT_CATEGORIES = [
  'BANK',
  'MPESA',
  'CASH',
  'CURRENT',
  'SAVING',
  'RECEIVABLE',
  'PAYABLE',
  'PRODUCTS'
]

export default {
  data() {
    return {
      rows: [],
      deletedRows: [],
      search: '',
      isImporting: false,
      progress: 0,
      processedRows: 0,
      totalRows: 0,
      ACCOUNT_TYPES,
      ACCOUNT_CATEGORIES
    }
  },

  computed: {
    filteredRows() {
      if (!this.search) return this.rows
      return this.rows.filter(r =>
        Object.values(r).some(v =>
          String(v).toLowerCase().includes(this.search.toLowerCase())
        )
      )
    }
  },

  methods: {
    handleFile(e) {
      const reader = new FileReader()
      reader.onload = ev => {
        const wb = XLSX.read(ev.target.result, { type: 'binary' })
        const sheet = wb.Sheets[wb.SheetNames[0]]
        const data = XLSX.utils.sheet_to_json(sheet, { defval: '' })

        this.rows = data.map(r => ({
          name: r['Account Name'] || '',
          type: (r['Account Type'] || '').toUpperCase(),
          accountCategory: (r['Account Category'] || '').toUpperCase(),
          openingBalance: Number(r['Opening Balance']) || 0,

          _errors: {},
          _duplicate: false,
          _status: 'PENDING',
          _remark: ''
        }))


        this.rows.forEach(this.validateRow)
        this.detectDuplicates()
      }

      reader.readAsBinaryString(e.target.files[0])
    },

    validateRow(r) {
      r._errors = {}

      if (!r.name) r._errors.name = true
      if (!ACCOUNT_TYPES.includes(r.type)) r._errors.type = true
      if (!ACCOUNT_CATEGORIES.includes(r.accountCategory)) r._errors.accountCategory = true
      if (isNaN(r.openingBalance)) r._errors.openingBalance = true

      this.detectDuplicates()
    },

    detectDuplicates() {
      const seen = {}
      this.rows.forEach(r => {
        r._duplicate = false
        const key = `${r.name}-${r.accountCategory}`
        if (r.name && r.accountCategory && seen[key]) r._duplicate = true
        seen[key] = true
      })
    },

    deleteRow(i) {
      this.deletedRows.push(this.rows[i])
      this.rows.splice(i, 1)
    },

    undoDelete() {
      this.rows.push(...this.deletedRows)
      this.deletedRows = []
    },

    rowClass(r) {
      if (r._status === 'SUCCESS') return 'table-success'
      if (r._status === 'FAILED') return 'table-danger'
      if (r._duplicate) return 'table-warning'
      if (Object.keys(r._errors).length) return 'table-danger'
      return ''
    },

    async downloadSample() {
      const wb = new ExcelJS.Workbook()
      const sheet = wb.addWorksheet('AccountOpening')

      sheet.columns = [
        { header: 'Account Name', key: 'name' },
        { header: 'Account Type', key: 'type' },
        { header: 'Account Category', key: 'accountCategory' },
        { header: 'Opening Balance', key: 'openingBalance' }
      ]


      sheet.addRow(['Cash at Bank', 'ASSET', 'BANK', 250000])
      sheet.getRow(1).font = { bold: true }
      sheet.views = [{ state: 'frozen', ySplit: 1 }]

      const lookup = wb.addWorksheet('LOOKUPS', { state: 'hidden' })
      lookup.getColumn(1).values = ['TYPE', ...ACCOUNT_TYPES]
      lookup.getColumn(2).values = ['CATEGORY', ...ACCOUNT_CATEGORIES]

      sheet.dataValidations.add('B2:B1000', {
        type: 'list',
        formulae: ['LOOKUPS!$A$2:$A$6']
      })

      sheet.dataValidations.add('C2:C1000', {
        type: 'list',
        formulae: ['LOOKUPS!$B$2:$B$9']
      })

      const buffer = await wb.xlsx.writeBuffer()
      saveAs(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
        'account_opening_template.xlsx'
      )
    },

    async submitRows() {
      const validRows = this.rows
        .map((r, index) => ({ r, index }))
        .filter(x => !x.r._duplicate && Object.keys(x.r._errors).length === 0)

      if (!validRows.length) return alert('No valid rows')

      this.isImporting = true
      this.processedRows = 0
      this.totalRows = validRows.length

      for (let i = 0; i < validRows.length; i += 10) {
        const chunk = validRows.slice(i, i + 10)
        try {
          const res = await accountApi.importBatch(chunk.map(x => ({
            index: x.index,
            name: x.r.name,
            type: x.r.type,
            accountCategory: x.r.accountCategory,
            openingBalance: x.r.openingBalance
          })))

          res.data.forEach(r => {
            this.rows[r.index]._status = r.status
            this.rows[r.index]._remark = r.message
          })
        } catch {
          chunk.forEach(x => {
            x.r._status = 'FAILED'
            x.r._remark = 'System error'
          })
        }

        this.processedRows += chunk.length
        this.progress = Math.round((this.processedRows / this.totalRows) * 100)
      }

      this.isImporting = false
    }
  }
}
</script>
